import {
  forwardRef,
  type HTMLAttributes,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import type {
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
} from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import {
  KibChevronDownIcon,
  KibChevronUpIcon,
  KibChewyIcon,
} from '@chewy/kib-icons-react'
import classNames from 'classnames'
import { LanguageUtils, type Nil, NumberUtils } from '@pbt/pbt-ui-components'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import ChirpWarningIcon from '~/components/common/icons/ChirpWarningIcon'
import { KibUserSelect } from '~/components/common/inputs/user-select/KibUserSelect'
import {
  getIsRetailOrderLineItem,
  getItemCoveredByWplan,
} from '~/components/dashboard/invoices/invoiceUtils'
import ChargesActions from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/ChargesActions'
import DragHandleIcon from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/DragHandleIcon'
import NumberField from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/NumberField'
import type { OptimisticOverride } from '~/components/dashboard/soapV2/charges/table/hooks/useOptimisticItemUpdates'
import MembershipIconWithTooltip from '~/components/dashboard/soapV2/charges/table/MembershipIconWithTooltip'
import { getIsEditingChargesItem } from '~/store/duck/clientFinanceData'
import { useHasGroupedParentWithFixedPrice } from '~/store/hooks/charges'
import { getInvoiceGroupIdIsExpanded } from '~/store/reducers/finance'
import type { InvoiceLineItem } from '~/types'

import { useShouldHideDiscountCalculator } from '../utils/shouldHideDiscountCalculator'
import { useChargeItemProducerEdit } from './hooks/useChargeItemProducerEdit'
import { useChargeItemQuantityEdit } from './hooks/useChargeItemQuantityEdit'
import { usePrescriptionIconLogic } from './hooks/usePrescriptionIconLogic'
import { isPartiallyDeclinedBundle } from './utils/tableHelpers'

import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import '@chewy/kib-fields-new-styles/dist/kib-fields-new.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './ChirpSoapChargesTable.module.scss'

// Helper functions to reduce cognitive complexity
const getItemDisplayName = (
  item: InvoiceLineItem | RetailOrderLineItem,
  isBundle: boolean,
): string => {
  if (isBundle) {
    const invoiceItem = item as InvoiceLineItem
    return (
      invoiceItem.groupName || LanguageUtils.getTranslatedFieldName(item) || ''
    )
  }
  return LanguageUtils.getTranslatedFieldName(item) || ''
}

const hasItemDiscount = (
  item: InvoiceLineItem | RetailOrderLineItem,
  isRetailOrderLineItem: boolean,
): boolean => {
  if (isRetailOrderLineItem) return false

  const invoiceItem = item as InvoiceLineItem
  return (
    (invoiceItem.discountAmount != null && invoiceItem.discountAmount > 0) ||
    (invoiceItem.discountPerc != null && invoiceItem.discountPerc > 0)
  )
}

const calculateOriginalPrice = (
  item: InvoiceLineItem | RetailOrderLineItem,
  isRetailOrderLineItem: boolean,
  isWplanItem: boolean,
  coveredByWplan: boolean,
): number => {
  // For wellness items that are covered, use the extendedPrice as the original price
  if (isWplanItem && coveredByWplan && !isRetailOrderLineItem) {
    const invoiceItem = item as InvoiceLineItem
    return invoiceItem.extendedPrice || 0
  }

  const hasDiscount = hasItemDiscount(item, isRetailOrderLineItem)
  if (!hasDiscount || isRetailOrderLineItem) {
    return item.subTotal || 0
  }

  const invoiceItem = item as InvoiceLineItem
  const currentSubTotal = item.subTotal || 0

  if (invoiceItem.discountAmount != null && invoiceItem.discountAmount > 0) {
    return currentSubTotal + invoiceItem.discountAmount
  }

  if (invoiceItem.discountPerc != null && invoiceItem.discountPerc > 0) {
    // discountPerc is stored as decimal (e.g., 0.15 for 15%)
    // originalPrice * (1 - discountPerc) = currentSubTotal
    // originalPrice = currentSubTotal / (1 - discountPerc)
    // Guard against 100% discount (discountPerc = 1.0) to prevent division by zero
    if (invoiceItem.discountPerc >= 1) {
      return currentSubTotal
    }
    return currentSubTotal / (1 - invoiceItem.discountPerc)
  }

  return currentSubTotal
}

export interface ChirpSoapChargesTableRowProps
  extends HTMLAttributes<HTMLTableRowElement> {
  applyOptimistic?: (itemId: string, changes: OptimisticOverride) => void
  bundleItems?: InvoiceLineItem[]
  dragHandleProps?: DraggableProvidedDragHandleProps | null
  draggableProps?: DraggableProvidedDraggableProps | null
  editDisabled?: boolean
  isBundle?: boolean
  isDragging?: boolean
  isNested?: boolean
  item: InvoiceLineItem | RetailOrderLineItem
  onBundleDeclineClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onBundleDeleteClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onCalculatorClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onDeclineClick?: (
    item: InvoiceLineItem | RetailOrderLineItem,
    parentItem?: InvoiceLineItem | RetailOrderLineItem,
  ) => void
  onDeleteClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onPrescriptionClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onToggleExpand?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onVaccineClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  parentItem?: InvoiceLineItem | RetailOrderLineItem
  soapId?: string | Nil
}

const ChirpSoapChargesTableRow = forwardRef<
  HTMLTableRowElement,
  ChirpSoapChargesTableRowProps
>(
  (
    {
      applyOptimistic,
      bundleItems,
      dragHandleProps,
      draggableProps,
      editDisabled = false,
      isBundle = false,
      isDragging = false,
      isNested = false,
      item,
      onBundleDeclineClick,
      onBundleDeleteClick,
      onCalculatorClick,
      onDeclineClick,
      onDeleteClick,
      onPrescriptionClick,
      onToggleExpand,
      onVaccineClick,
      parentItem,
      soapId,
      className,
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const isRetailOrderLineItem = getIsRetailOrderLineItem(item)
    const hasParentWithFixedPrice =
      useHasGroupedParentWithFixedPrice(parentItem)
    const shouldHideCalculator = useShouldHideDiscountCalculator(
      item,
      parentItem,
      editDisabled,
    )
    const isEditingChargesItem = useSelector(getIsEditingChargesItem(item.id))

    const disabled = editDisabled || isEditingChargesItem

    // Get prescription icon logic from custom hook
    const {
      displayChewyIcon,
      shouldShowChewyIcon,
      shouldShowWarningIcon,
      warningTooltipText,
      isPrescribable,
      isVaccine,
    } = usePrescriptionIconLogic({
      item,
      isBundle,
      onVaccineClick,
    })

    // Get expand state for bundles - use group property if available, fallback to id
    const groupId = isRetailOrderLineItem
      ? null
      : (item as InvoiceLineItem).group
    const expandKey = groupId || item.id
    const isExpanded = useSelector(
      getInvoiceGroupIdIsExpanded(expandKey, soapId),
    )

    // Get item name - bundles use groupName, regular items use name/nameTranslation
    const baseItemName = getItemDisplayName(item, isBundle)

    // Check if bundle is partially declined using helper function
    const isPartiallyDeclinedInDeclinedSection = isPartiallyDeclinedBundle(item)
    const itemName = isPartiallyDeclinedInDeclinedSection
      ? `[${t('Common:PARTIAL')}] ${baseItemName}`
      : baseItemName

    // Determine if we should display wellness icon
    const isWplanItem = isRetailOrderLineItem
      ? false
      : Boolean((item as InvoiceLineItem).wplanLogId)
    const coveredByWplan = getItemCoveredByWplan(item)

    // Calculate original price
    const originalPrice = calculateOriginalPrice(
      item,
      isRetailOrderLineItem,
      isWplanItem,
      coveredByWplan,
    )

    const hasDiscount = hasItemDiscount(item, isRetailOrderLineItem)
    const shouldShowOriginalPrice =
      hasDiscount || (isWplanItem && coveredByWplan)

    // Use shared hooks for quantity and producer changes with optimistic updates
    const { onChange: handleQuantityChange } = useChargeItemQuantityEdit(
      item,
      applyOptimistic,
    )
    const handleProducerChange = useChargeItemProducerEdit(
      item,
      undefined,
      applyOptimistic,
    )

    const handleExpandClick = (e: ReactMouseEvent) => {
      e.stopPropagation()
      if (onToggleExpand) {
        onToggleExpand(item)
      }
    }

    const handlePrescriptionClick = (e: ReactMouseEvent) => {
      e.stopPropagation()
      if (onPrescriptionClick) {
        onPrescriptionClick(item)
      }
    }

    const handleVaccineClick = (e: ReactMouseEvent) => {
      e.stopPropagation()
      if (onVaccineClick) {
        onVaccineClick(item)
      }
    }

    // NOTE: Must return single <tr> element for react-beautiful-dnd to work correctly.
    // For bundles with children, we render them inside a nested table within a single <td colspan>
    if (isBundle && bundleItems && bundleItems.length > 0) {
      return (
        <tr
          className={classNames(
            styles.dataRow,
            {
              [styles.dataRowDragging]: isDragging,
              [styles.declined]: item.declined,
            },
            className,
          )}
          ref={ref}
          {...draggableProps}
          {...dragHandleProps}
        >
          <td className={styles.bundleCell} colSpan={6}>
            {/* Parent bundle row content - using table for proper column alignment */}
            <table className={styles.bundleParentTable}>
              <colgroup>
                <col style={{ width: '307px' }} />
                <col style={{ width: '176px' }} />
                <col style={{ width: '55px' }} />
                <col style={{ width: '80px' }} />
                <col style={{ width: '55px' }} />
                <col style={{ width: '45px' }} />
              </colgroup>
              <tbody>
                <tr className={styles.bundleParentRow}>
                  {/* Item column with drag handle and expand button */}
                  <td
                    className={classNames(styles.dataCell, styles.dataCellItem)}
                  >
                    <span className={styles.dragHandle}>
                      <DragHandleIcon size={24} />
                    </span>
                    <KibButtonNew
                      aria-label={
                        isExpanded
                          ? t('Common:COLLAPSE_BUNDLE')
                          : t('Common:EXPAND_BUNDLE')
                      }
                      className={styles.expandButton}
                      emphasis="tertiary"
                      icon={
                        isExpanded ? (
                          <KibChevronUpIcon />
                        ) : (
                          <KibChevronDownIcon />
                        )
                      }
                      onClick={handleExpandClick}
                    />
                    <div className={styles.itemNameWithIcons}>
                      {displayChewyIcon && (
                        <KibChewyIcon className={styles.chewyIcon} />
                      )}
                      <span className={styles.itemName} title={itemName}>
                        {itemName}
                      </span>
                    </div>
                  </td>

                  {/* Empty Producer column */}
                  <td
                    aria-label={t('Common:PRODUCER_NOT_APPLICABLE_FOR_BUNDLES')}
                    className={styles.dataCell}
                  />

                  {/* Empty Quantity column */}
                  <td
                    aria-label={t('Common:QUANTITY_NOT_APPLICABLE_FOR_BUNDLES')}
                    className={styles.dataCell}
                  />

                  {/* Actions column */}
                  <td
                    className={classNames(
                      styles.dataCell,
                      styles.dataCellActions,
                    )}
                  >
                    {!isPartiallyDeclinedInDeclinedSection && (
                      <ChargesActions
                        showDeclineButton
                        showDeleteButton
                        declineHighlighted={item.declined}
                        disabledDecline={disabled}
                        disabledDelete={disabled}
                        showCalculatorButton={false}
                        onDeclineClick={() => onBundleDeclineClick?.(item)}
                        onDeleteClick={() => onBundleDeleteClick?.(item)}
                      />
                    )}
                  </td>

                  {/* Total column */}
                  <td
                    className={classNames(
                      styles.dataCell,
                      styles.dataCellTotal,
                    )}
                  >
                    {!isPartiallyDeclinedInDeclinedSection && (
                      <div className={styles.priceContainer}>
                        {isWplanItem && coveredByWplan && (
                          <MembershipIconWithTooltip
                            className={styles.membershipIcon}
                            coveredByWplan={coveredByWplan}
                            tooltipText={t(
                              'Tooltips:COVERED_BY_MEMBERSHIP_PLAN',
                            )}
                          />
                        )}
                        <span className={styles.price}>
                          {NumberUtils.formatMoney(item.subTotal)}
                        </span>
                      </div>
                    )}
                  </td>

                  {/* Original Price column */}
                  <td
                    className={classNames(
                      styles.dataCell,
                      styles.dataCellOriginalPrice,
                    )}
                  >
                    {shouldShowOriginalPrice && (
                      <span className={styles.originalPrice}>
                        {NumberUtils.formatMoney(originalPrice)}
                      </span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Nested table for bundle children */}
            {isExpanded && (
              <table className={styles.nestedTable}>
                <tbody>
                  {bundleItems.map((childItem) => (
                    <ChirpSoapChargesTableRow
                      isNested
                      applyOptimistic={applyOptimistic}
                      editDisabled={disabled}
                      item={childItem}
                      key={`${childItem.bundleId}_${childItem.id}`}
                      parentItem={item as InvoiceLineItem}
                      soapId={soapId}
                      onCalculatorClick={onCalculatorClick}
                      onDeclineClick={onDeclineClick}
                      onDeleteClick={onDeleteClick}
                      onPrescriptionClick={onPrescriptionClick}
                      onVaccineClick={onVaccineClick}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )
    }

    // Regular (non-bundle) or nested row
    return (
      <tr
        className={classNames(
          styles.dataRow,
          {
            [styles.dataRowDragging]: isDragging,
            [styles.dataRowNested]: isNested,
            [styles.declined]: item.declined,
          },
          className,
        )}
        ref={ref}
        {...draggableProps}
        {...dragHandleProps}
      >
        {/* Item cell with drag handle and expand icon */}
        <td className={styles.dataCell}>
          <div
            className={classNames(styles.dataCellItem, {
              [styles.dataCellItemNested]: isNested,
            })}
          >
            {!isNested && (
              <span className={styles.dragHandle}>
                <DragHandleIcon size={24} />
              </span>
            )}
            {isBundle && (
              <KibButtonNew
                aria-label={
                  isExpanded
                    ? t('Common:COLLAPSE_BUNDLE')
                    : t('Common:EXPAND_BUNDLE')
                }
                className={styles.expandButton}
                emphasis="tertiary"
                icon={
                  isExpanded ? <KibChevronUpIcon /> : <KibChevronDownIcon />
                }
                onClick={handleExpandClick}
              />
            )}
            {isPrescribable && onPrescriptionClick ? (
              <>
                {/* For Chewy + Approved: show Chewy icon only (no warning) */}
                {shouldShowChewyIcon && (
                  <KibChewyIcon className={styles.chewyIcon} />
                )}
                {/* Show warning icon based on logic */}
                {shouldShowWarningIcon && (
                  <ChirpWarningIcon
                    className={styles.warningIcon}
                    size="large"
                    tooltipText={warningTooltipText}
                  />
                )}
                <KibButtonNew
                  className={styles.prescriptionLink}
                  emphasis="tertiary"
                  title={itemName}
                  onClick={handlePrescriptionClick}
                >
                  {itemName}
                </KibButtonNew>
              </>
            ) : isVaccine && onVaccineClick ? (
              <>
                {displayChewyIcon && (
                  <KibChewyIcon className={styles.chewyIcon} />
                )}
                <KibButtonNew
                  className={styles.prescriptionLink}
                  emphasis="tertiary"
                  title={itemName}
                  onClick={handleVaccineClick}
                >
                  {itemName}
                </KibButtonNew>
              </>
            ) : (
              <>
                {displayChewyIcon && (
                  <KibChewyIcon className={styles.chewyIcon} />
                )}
                <span className={styles.itemName} title={itemName}>
                  {itemName}
                </span>
              </>
            )}
          </div>
        </td>

        {/* Producer cell */}
        <td
          className={classNames(styles.dataCell, styles.dataCellProducer)}
          onClick={(e) => e.stopPropagation()}
        >
          {!item.declined && !isBundle && (
            <KibUserSelect
              reset
              disabled={disabled}
              placeHolder=""
              userSelectStateId={`producer-${item.id}`}
              value={item.producerId || ''}
              onChange={handleProducerChange}
            />
          )}
        </td>

        {/* Quantity cell */}
        <td
          className={classNames(styles.dataCell, styles.dataCellQuantity)}
          onClick={(e) => e.stopPropagation()}
        >
          {!item.declined && !isBundle && (
            <NumberField
              disabled={disabled}
              label=""
              min={0}
              step="any"
              value={String(item.quantity || 0)}
              onChange={handleQuantityChange}
            />
          )}
        </td>

        {/* Actions cell */}
        <td
          className={classNames(styles.dataCell, styles.dataCellActions)}
          onClick={(e) => e.stopPropagation()}
        >
          {!isBundle && (
            <ChargesActions
              declineHighlighted={item.declined}
              disabledCalculator={disabled}
              disabledDecline={disabled}
              disabledDelete={disabled}
              showCalculatorButton={!shouldHideCalculator}
              onCalculatorClick={() => onCalculatorClick?.(item)}
              onDeclineClick={() => onDeclineClick?.(item, parentItem)}
              onDeleteClick={() => onDeleteClick?.(item)}
            />
          )}
        </td>

        {/* Total cell */}
        <td className={classNames(styles.dataCell, styles.dataCellTotal)}>
          {!hasParentWithFixedPrice && (
            <div className={styles.priceContainer}>
              {isWplanItem && coveredByWplan && (
                <MembershipIconWithTooltip
                  className={styles.membershipIcon}
                  coveredByWplan={coveredByWplan}
                  tooltipText={t('Tooltips:COVERED_BY_MEMBERSHIP_PLAN')}
                />
              )}
              <span className={styles.price}>
                {NumberUtils.formatMoney(item.subTotal)}
              </span>
            </div>
          )}
        </td>

        {/* Original Price cell */}
        <td
          className={classNames(styles.dataCell, styles.dataCellOriginalPrice)}
        >
          {!hasParentWithFixedPrice && shouldShowOriginalPrice && (
            <span className={styles.originalPrice}>
              {NumberUtils.formatMoney(originalPrice)}
            </span>
          )}
        </td>
      </tr>
    )
  },
)

export default ChirpSoapChargesTableRow
